import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion } from '@/components/Accordion';
import { Skeleton } from '@/components/Skeleton';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { findSection } from '@/lib/cms/section-types';
import { getCmsDocument, type CmsPageLocale } from '@/lib/db/cms-pages';
import { buildFaqPageJsonLd, FAQ_META } from '@/lib/seo/site';
import { useSeoHead } from '@/lib/seo/useSeoHead';

type FaqItem = { id?: string; category: string; question: string; answer: string };

const CATEGORIES = [
  'all',
  'concept',
  'process',
  'price',
  'offPlan',
  'security',
  'payment',
] as const;
type Category = (typeof CATEGORIES)[number];

export function FaqPage() {
  const { t, i18n } = useTranslation();
  const [activeCat, setActiveCat] = useState<Category>('all');

  const locale = (
    ['en', 'fr', 'ar'].includes(i18n.language?.split('-')[0] ?? '')
      ? (i18n.language.split('-')[0] as CmsPageLocale)
      : 'en'
  ) as CmsPageLocale;

  const [cmsItems, setCmsItems] = useState<FaqItem[] | null>(null);
  const [cmsTitle, setCmsTitle] = useState<string | null>(null);
  const [cmsLead, setCmsLead] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // CMS copy is strict: loading / absent / hidden / emptied → not rendered,
  // no i18n fallback (the per-category empty state below stays — functional
  // wording, not CMS content).
  useEffect(() => {
    setLoading(true);
    getCmsDocument('faq', locale)
      .then((doc) => {
        const intro = findSection(doc, 'rich_text');
        const list = findSection(doc, 'faq_list');
        setCmsTitle(intro && intro.visible ? intro.title || null : null);
        setCmsLead(intro && intro.visible ? intro.html || null : null);
        if (list && list.visible) setCmsItems(list.items.filter((it) => it.visible));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [locale]);

  // FAQPage JSON-LD only when the CMS actually provides Q&A content —
  // approximate structured data is worse than none.
  const faqJsonLd = useMemo(() => (cmsItems ? buildFaqPageJsonLd(cmsItems) : null), [cmsItems]);
  useSeoHead(faqJsonLd ? { ...FAQ_META, jsonLd: [faqJsonLd] } : FAQ_META);

  const items = cmsItems ?? [];
  const filtered = activeCat === 'all' ? items : items.filter((it) => it.category === activeCat);

  return (
    <div className="public">
      <section className="faq-hero">
        <div className="faq-hero__kicker">{t('faq.kicker')}</div>
        {cmsTitle && <h1 className="faq-hero__title">{cmsTitle}</h1>}
        {cmsLead && (
          <div className="faq-hero__lead" dangerouslySetInnerHTML={{ __html: cmsLead }} />
        )}
      </section>

      <div className="faq-tabs" role="tablist" aria-label="FAQ categories">
        <div className="faq-tabs__inner">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={activeCat === cat}
              className={`faq-tabs__btn${activeCat === cat ? ' is-active' : ''}`}
              onClick={() => setActiveCat(cat)}
            >
              {t(`faq.categories.${cat}`)}
            </button>
          ))}
        </div>
      </div>

      <section className="faq-section">
        <div className="faq-list">
          {loading ? (
            <FaqListSkeleton />
          ) : filtered.length === 0 ? (
            <p className="faq-empty">{t('faq.empty')}</p>
          ) : (
            filtered.map((item, i) => (
              <Accordion
                key={item.id ?? `${item.category}-${i}`}
                question={item.question}
                answer={item.answer}
                defaultOpen={i === 0}
              />
            ))
          )}
        </div>

        <div className="faq-callout">
          <div>
            <h2 className="faq-callout__title">{t('faq.callout.title')}</h2>
            <p className="faq-callout__lead">{t('faq.callout.lead')}</p>
          </div>
          <WhatsAppButton label={t('actions.contactUs')} size="md" trackSource="faq" />
        </div>
      </section>
    </div>
  );
}

// Mirrors the .accordion__trigger row (padding, bottom border, question +
// chevron) so the list reserves its height while the CMS FAQ document loads —
// instead of briefly flashing the "no FAQs" empty state.
function FaqListSkeleton() {
  return (
    <div aria-busy="true">
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          style={{
            padding: '24px 0',
            borderBottom: '1px solid var(--ddm-neutral-100)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <Skeleton width={`${68 - i * 6}%`} height={18} />
          <Skeleton width={16} height={18} />
        </div>
      ))}
    </div>
  );
}
